import {
  TElementTranslation,
  TFrameTranslation,
  TStoryElement,
  TStoryFrame,
} from 'context/StudioEditorProvider';
import { DEFAULT_STORY_LOCALE, STORY_LOCALE_CODES, TStoryLocale } from 'constants/storyLocales';
import { resolveStoryDuration } from 'constants/storyDuration';
import { TStoryCoverWire } from 'types/storyWire';

type TElementTranslations = Partial<Record<TStoryLocale, TElementTranslation>>;

/**
 * Every element field that varies by locale. Not just copy: the CTA actions localize too, so a
 * translated slide can send the user somewhere translated.
 */
const LOCALIZED_FIELDS = [
  'content',
  'question',
  'reveal',
  'endedText',
  'ctaMobile',
  'ctaWeb',
] as const;

export const localizeElement = (element: TStoryElement, locale: TStoryLocale): TStoryElement => {
  const overrides = element.translations?.[locale];
  if (!overrides || locale === DEFAULT_STORY_LOCALE) {
    return element;
  }
  const pick = <T>(base: T | undefined, override: T | undefined): T | undefined =>
    override === undefined || override === '' ? base : override;

  return {
    ...element,
    content: pick(element.content, overrides.content),
    question: pick(element.question, overrides.question),
    options: overrides.options
      ? (element.options ?? []).map((base, i) => pick(base, overrides.options?.[i]) ?? base)
      : element.options,
    reveal: pick(element.reveal, overrides.reveal),
    endedText: pick(element.endedText, overrides.endedText),
    ctaMobile: pick(element.ctaMobile, overrides.ctaMobile),
    ctaWeb: pick(element.ctaWeb, overrides.ctaWeb),
  };
};

/**
 * The frame as one locale sees it: its own backdrop and duration where it has them, English
 * otherwise.
 *
 * The two overrides are independent, and they resolve differently. The backdrop has no per-field
 * fallback — a bucket replaces `background` AND `cover` together; see `TFrameTranslation` for why.
 * The duration is a lone scalar in its own map, so it falls back on its own. `name`, `statusBar`
 * and `elements` are shared and pass through untouched.
 */
export const localizeFrame = (frame: TStoryFrame, locale: TStoryLocale): TStoryFrame => {
  if (locale === DEFAULT_STORY_LOCALE) {
    return frame;
  }
  const override = frame.translations?.[locale];
  const durationSeconds = frame.durations?.[locale];
  if (!override && durationSeconds === undefined) {
    return frame;
  }
  return {
    ...frame,
    ...(override ? { background: override.background, cover: override.cover } : {}),
    durationSeconds: durationSeconds ?? frame.durationSeconds,
  };
};

export const setLocaleField = (
  translations: TElementTranslations | undefined,
  locale: TStoryLocale,
  patch: Partial<TElementTranslation>
): TElementTranslations => ({
  ...translations,
  [locale]: { ...translations?.[locale], ...patch },
});

export const localizedString = (
  base: string,
  perLocale?: Partial<Record<TStoryLocale, string | undefined>>
): Record<string, string> => {
  const result: Record<string, string> = { [DEFAULT_STORY_LOCALE]: base };
  if (perLocale) {
    (Object.keys(perLocale) as TStoryLocale[]).forEach((locale) => {
      const value = perLocale[locale];
      if (locale !== DEFAULT_STORY_LOCALE && value && value.trim()) {
        result[locale] = value;
      }
    });
  }
  return result;
};

export const localizedStringList = (
  base: string[],
  perLocale?: Partial<Record<TStoryLocale, string[] | undefined>>
): Record<string, string>[] =>
  base.map((value, index) => {
    const forIndex: Partial<Record<TStoryLocale, string | undefined>> = {};
    (Object.keys(perLocale ?? {}) as TStoryLocale[]).forEach((locale) => {
      forIndex[locale] = perLocale?.[locale]?.[index];
    });
    return localizedString(value, forIndex);
  });

/*
 * Expand / collapse: the two directions of the wire boundary for element copy.
 *
 * The wire groups slides by locale, each bucket a complete story with plain-string copy. The editor
 * keeps one canonical story plus a `translations` bucket per element, so the boundary expands into
 * per-locale copies on the way out and collapses them back on the way in.
 *
 * `localizeElement` above is the expansion primitive — it already resolves one locale with base
 * fallback, and the canvas has been using it for the same purpose all along.
 */

/**
 * Which locales deserve their own bucket: `en`, plus any locale an author has actually authored
 * something in. A locale with nothing of its own is omitted rather than shipped as a byte-identical
 * copy of `en` — the device falls back to `en` when its bucket is missing.
 *
 * "Something" is translated copy, a backdrop override OR a duration override. Counting only copy
 * would drop a locale whose sole difference is its cover image or how long its slide stays up, and
 * the difference would silently never ship.
 */
export const localesWithContent = (frames: TStoryFrame[]): TStoryLocale[] => {
  const written = new Set<TStoryLocale>([DEFAULT_STORY_LOCALE]);

  frames.forEach((frame) => {
    STORY_LOCALE_CODES.forEach((locale) => {
      if (frame.translations?.[locale] || frame.durations?.[locale] !== undefined) {
        written.add(locale);
      }
    });

    frame.elements.forEach((element) => {
      STORY_LOCALE_CODES.forEach((locale) => {
        const bucket = element.translations?.[locale];
        if (!bucket) {
          return;
        }
        const hasText = LOCALIZED_FIELDS.some((field) => bucket[field]?.trim());
        const hasOptions = (bucket.options ?? []).some((option) => option?.trim());
        if (hasText || hasOptions) {
          written.add(locale);
        }
      });
    });
  });

  // Stable order, `en` first, so the payload does not churn between saves.
  return STORY_LOCALE_CODES.filter((locale) => written.has(locale));
};

/**
 * Folds one locale's element back into the canonical element's `translations` bucket.
 *
 * Only values that actually DIFFER from the base are recorded: a bucket the author never touched
 * arrives from the wire filled with English (expansion resolves every field), and storing that
 * would turn every untranslated field into a fake translation.
 */
export const collapseTranslations = (
  base: TStoryElement,
  translated: TStoryElement,
  locale: TStoryLocale
): TStoryElement => {
  if (locale === DEFAULT_STORY_LOCALE) {
    return base;
  }

  const patch: Partial<TElementTranslation> = {};
  LOCALIZED_FIELDS.forEach((field) => {
    const value = translated[field];
    if (typeof value === 'string' && value !== base[field]) {
      patch[field] = value;
    }
  });

  const { options } = translated;
  if (options && options.some((option, index) => option !== base.options?.[index])) {
    patch.options = [...options];
  }

  if (Object.keys(patch).length === 0) {
    return base;
  }
  return { ...base, translations: setLocaleField(base.translations, locale, patch) };
};

const COVER_KEYS = [
  'type',
  'mediaUrl',
  'posterUrl',
  'fit',
  'position',
  'fillColor',
] as const satisfies readonly (keyof TStoryCoverWire)[];

const sameCover = (a: TStoryCoverWire | undefined, b: TStoryCoverWire | undefined): boolean => {
  if (!a || !b) {
    return !a && !b;
  }
  return COVER_KEYS.every((key) => a[key] === b[key]);
};

/**
 * Folds one locale's backdrop back into the canonical frame's `translations` bucket.
 *
 * Field-compared rather than stringified: `UPDATE_FRAME_COVER` merges patches into `cover`, so key
 * order is not stable, and the `canonical` stringifier lives in `helpers/story/divergence.ts` —
 * importing it here would make the two modules circular.
 *
 * Nothing is recorded when the locale matches the base, which is the common case: expansion fills
 * every bucket with the English backdrop, and storing that would turn every untouched slide into
 * a fake override.
 */
export const collapseFrameTranslation = (
  base: TStoryFrame,
  translated: TStoryFrame,
  locale: TStoryLocale
): TStoryFrame => {
  if (locale === DEFAULT_STORY_LOCALE) {
    return base;
  }
  if (translated.background === base.background && sameCover(translated.cover, base.cover)) {
    return base;
  }

  const override: TFrameTranslation = {
    background: translated.background,
    cover: translated.cover,
  };
  return { ...base, translations: { ...base.translations, [locale]: override } };
};

/**
 * Folds one locale's duration back into the canonical frame's `durations` map.
 *
 * Both sides are resolved first: the wire value is always present and already clamped, while the
 * base may be absent and mean the 5s default. Comparing raw would record an override for every
 * locale of a frame that never set a duration at all.
 */
export const collapseFrameDuration = (
  base: TStoryFrame,
  durationSeconds: number | undefined,
  locale: TStoryLocale
): TStoryFrame => {
  if (locale === DEFAULT_STORY_LOCALE || durationSeconds === undefined) {
    return base;
  }
  const resolved = resolveStoryDuration(durationSeconds);
  if (resolved === resolveStoryDuration(base.durationSeconds)) {
    return base;
  }
  return { ...base, durations: { ...base.durations, [locale]: resolved } };
};
